import { useTranslation } from "react-i18next";
import { TableColumn } from "react-data-table-component";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  Input,
  Label,
} from "reactstrap";
import { useHistory } from "react-router-dom";
import { DebounceInput } from "react-debounce-input";
import { PaginateQuery } from "../../../models";
import {
  LectureCommentDetailDto,
  UpdateCommentStatusRequestDto,
} from "../../../interfaces";
import { EducationalStageDto } from "../../../interfaces/curriculum";
import { SelectListDto } from "../../../models";
import {
  ColumnHeader,
  DateColumn,
  AppTable,
  TableSpinner,
} from "../../../components";
import { BreadCrumb } from "../../../components/Common";
import ConfirmationModal from "../../../components/Common/ConfirmationModal";
import { CommentStatusEnum, RoleEnum } from "../../../enums";
import {
  useGetLectureCommentsQuery,
  useUpdateCommentStatusMutation,
  useDeleteLectureCommentMutation,
} from "../../../store/api/lectureCommentApi";
import { useGetEducationSystemSelectListQuery } from "../../../store/api/educationSystemApi";
import { useGetCurriculumSelectListQuery } from "../../../store/api/curriculumApi";
import { useGetCourseSelectListQuery } from "../../../store/api/courseApi";
import { useGetEducationalStagesQuery } from "../../../store/api/educationalStageApi";
import { useGetSubjectsSelectListByGradeQuery } from "../../../store/api/subjectApi";
import { AccountState } from "../../../store/slices/accountSlice";

const HIERARCHY_FILTER_KEYS = [
  "educationSystemId",
  "curriculumId",
  "courseId",
  "stageId",
  "gradeId",
  "subjectId",
] as const;

const labelOf = (
  item:
    | {
        value?: { en?: string; ar?: string };
        name?: { en?: string; ar?: string };
      }
    | undefined,
  langKey: "en" | "ar",
) =>
  item?.value?.[langKey] ||
  item?.value?.en ||
  item?.name?.[langKey] ||
  item?.name?.en ||
  "";

type TProps = {};
export const CommentReviewsPage: React.FC<TProps> = () => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const lang = i18n.language === "ar" ? "ar" : "en";
  const profile = useSelector(
    (state: { account: AccountState }) => state.account.userProfile,
  );

  const roleKey = profile?.role?.key;
  const isHeadTeacherAdmin = roleKey === RoleEnum.HEAD_TEACHER_ADMIN;
  const isSystemAdmin = roleKey === RoleEnum.SYSTEM_ADMIN;
  const isTeacherAdmin = roleKey === RoleEnum.TEACHER_ADMIN;

  const initState = new PaginateQuery();
  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>(initState);
  const [queryReady, setQueryReady] = useState(false);
  const [selectedComment, setSelectedComment] =
    useState<LectureCommentDetailDto | null>(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [filterStatusId, setFilterStatusId] = useState<string>("");

  const [educationSystemId, setEducationSystemId] = useState("");
  const [curriculumId, setCurriculumId] = useState("");
  const [courseId, setCourseId] = useState("");
  const [stageId, setStageId] = useState("");
  const [gradeId, setGradeId] = useState("");
  const [subjectId, setSubjectId] = useState("");

  const { data: educationSystems } = useGetEducationSystemSelectListQuery();
  const { data: curricula } = useGetCurriculumSelectListQuery(
    educationSystemId || undefined,
    { skip: !educationSystemId },
  );
  const { data: allCourses } = useGetCourseSelectListQuery();
  const courses = useMemo(() => {
    if (isTeacherAdmin && profile?.courses?.length) {
      const assignedIds = new Set(profile.courses.map((c) => c.id));
      return (allCourses || []).filter((c: any) => assignedIds.has(c.id));
    }
    return allCourses || [];
  }, [allCourses, isTeacherAdmin, profile?.courses]);

  const stagesQuery = useMemo(() => {
    const q = new PaginateQuery();
    q.limit = 1000;
    q.page = 1;
    q.sortBy = "sequence_number:ASC";
    q.filter = { curriculumId, courseId };
    return q;
  }, [curriculumId, courseId]);

  const { data: stagesResponse } = useGetEducationalStagesQuery(stagesQuery, {
    skip: !curriculumId || !courseId,
  });
  const stages: EducationalStageDto[] = stagesResponse?.data || [];
  const selectedStage = useMemo(
    () => stages.find((s) => s.id === stageId),
    [stages, stageId],
  );
  const grades = selectedStage?.grades || [];

  const { data: gradeSubjects = [] } = useGetSubjectsSelectListByGradeQuery(
    gradeId,
    { skip: !gradeId },
  );

  const assignedEducationSystems = useMemo(() => {
    if (!isHeadTeacherAdmin && !isSystemAdmin) return [];
    return educationSystems || [];
  }, [isHeadTeacherAdmin, isSystemAdmin, educationSystems]);

  const assignedCourses = useMemo(() => {
    if (!isTeacherAdmin) return [];
    return profile?.courses || [];
  }, [isTeacherAdmin, profile?.courses]);

  const hierarchyFilters = useMemo(() => {
    const filters: { [column: string]: string | string[] } = {};
    if (educationSystemId) filters.educationSystemId = educationSystemId;
    if (curriculumId) filters.curriculumId = curriculumId;
    if (courseId) filters.courseId = courseId;
    if (stageId) filters.stageId = stageId;
    if (gradeId) filters.gradeId = gradeId;
    if (subjectId) filters.subjectId = subjectId;
    return filters;
  }, [educationSystemId, curriculumId, courseId, stageId, gradeId, subjectId]);

  const hasHierarchyFilters = Object.keys(hierarchyFilters).length > 0;

  const apiQuery = useMemo(() => {
    const filter: { [column: string]: string | string[] } = {
      ...(paginateQuery.filter || {}),
    };
    HIERARCHY_FILTER_KEYS.forEach((key) => {
      delete filter[key];
    });
    Object.assign(filter, hierarchyFilters);
    if (filterStatusId) {
      filter.status = filterStatusId;
    } else {
      delete filter.status;
    }
    return {
      ...paginateQuery,
      filter,
    };
  }, [paginateQuery, hierarchyFilters, filterStatusId]);

  const {
    data: queryData,
    isLoading: isLoadingComments,
    refetch,
  } = useGetLectureCommentsQuery(apiQuery, { skip: !queryReady });

  const [
    updateCommentStatus,
    { isLoading: isUpdatingStatus, isSuccess: isUpdateStatusSuccess },
  ] = useUpdateCommentStatusMutation();

  const [deleteComment, { isLoading: isDeleting, isSuccess: isDeleteSuccess }] =
    useDeleteLectureCommentMutation();

  document.title = `${t("documentTitle", { pageName: "commentReviews" })}`;

  const getStatusBadge = (status: CommentStatusEnum) => {
    const color =
      status === CommentStatusEnum.APPROVED
        ? "success"
        : status === CommentStatusEnum.REJECTED
          ? "danger"
          : "warning";
    return <Badge color={color}>{t(status)}</Badge>;
  };

  const UserCell = ({ row }: { row: LectureCommentDetailDto }) => {
    const u = row.user;
    if (!u) return <span className="text-muted">—</span>;
    const name = u.name?.[lang] || u.name?.en || u.name?.ar || "—";
    return (
      <div className="d-flex align-items-center gap-2">
        {u.photo ? (
          <img
            src={u.photo}
            alt=""
            className="rounded-circle"
            style={{ width: 36, height: 36, objectFit: "cover" }}
          />
        ) : (
          <div
            className="rounded-circle d-flex align-items-center justify-content-center bg-secondary bg-opacity-25 text-secondary"
            style={{ width: 36, height: 36, fontSize: "0.875rem" }}
          >
            {name.charAt(0).toUpperCase()}
          </div>
        )}
        <div className="min-w-0">
          <div className="fw-medium text-truncate" style={{ maxWidth: 260 }}>
            {name}
          </div>
          <small
            className="text-muted text-truncate d-block"
            style={{ maxWidth: 260 }}
          >
            <a
              style={{ cursor: "pointer", direction: "ltr" }}
              href={`tel:+${u.phone?.countryCode}${u.phone?.phoneNumber}`}
            >
              <u
                style={{ direction: "ltr" }}
              >{`+${u.phone?.countryCode}${u.phone?.phoneNumber}`}</u>
            </a>
          </small>
        </div>
      </div>
    );
  };

  const LectureCell = ({ row }: { row: LectureCommentDetailDto }) => {
    const lec = row.lecture;
    if (!lec) return <span className="text-muted">—</span>;
    const subject = lec.lesson?.chapter?.subject?.name?.[lang];
    const chapter = lec.lesson?.chapter?.name?.[lang];
    const lesson = lec.lesson?.name?.[lang];
    const lectureName = lec.name?.[lang];
    const thumb = lec.video?.thumbnail;

    return (
      <div className="d-flex gap-2 align-items-start py-1 min-w-0 lecture-cell">
        {thumb && (
          <img
            src={thumb}
            alt=""
            className="rounded flex-shrink-0 border d-none d-sm-block"
            style={{ width: 64, height: 36, objectFit: "cover" }}
          />
        )}
        <div className="min-w-0 flex-grow-1 small">
          {subject && (
            <div
              className="fw-semibold text-primary text-truncate"
              title={subject}
            >
              {subject}
            </div>
          )}
          {chapter && (
            <div className="text-muted text-truncate" title={chapter}>
              <span className="me-1">›</span>
              {chapter}
            </div>
          )}
          {lesson && (
            <div className="text-muted text-truncate" title={lesson}>
              <span className="me-1">›</span>
              {lesson}
            </div>
          )}
          {lectureName && (
            <div
              className="fw-medium text-body text-truncate mt-1"
              title={lectureName}
            >
              <span className="text-muted me-1">›</span>
              {lectureName}
            </div>
          )}
          {!subject && !chapter && !lesson && !lectureName && (
            <div className="text-muted">—</div>
          )}
        </div>
      </div>
    );
  };

  const columns: TableColumn<LectureCommentDetailDto>[] = [
    {
      id: "comment",
      name: <ColumnHeader title="comment" />,
      cell: (row) => (
        <div className="text-break py-1 comment-cell">{row.comment || "—"}</div>
      ),
      sortable: false,
      minWidth: "160px",
      width: "20%",
    },
    {
      id: "user",
      name: <ColumnHeader title="user" />,
      cell: (row) => <UserCell row={row} />,
      sortable: false,
    },
    {
      id: "lecture",
      name: <ColumnHeader title="lecture" />,
      cell: (row) => <LectureCell row={row} />,
      sortable: false,
    },
    {
      id: "status",
      name: <ColumnHeader title="status" />,
      cell: (row) => getStatusBadge(row.status),
      sortable: false,
      minWidth: "95px",
      width: "95px",
    },
    {
      id: "created_at",
      name: <ColumnHeader title="createdAt" />,
      cell: (row) => <DateColumn date={new Date(row.createdAt)} />,
      sortable: true,
      minWidth: "110px",
      width: "120px",
    },
    {
      id: "action",
      name: <ColumnHeader title="action" />,
      sortable: false,
      minWidth: "70px",
      width: "70px",
      cell: (row) => (
        <UncontrolledDropdown>
          <DropdownToggle tag="a" id={`dropdown-${row.id}`} role="button">
            <i className="ri-more-fill fs-17 dropdown-menu-button-custom"></i>
          </DropdownToggle>
          <DropdownMenu>
            {row.status !== CommentStatusEnum.APPROVED && (
              <DropdownItem
                className="dropdown-item"
                onClick={() => {
                  setSelectedComment(row);
                  setShowApproveModal(true);
                }}
              >
                {t("approve")}
              </DropdownItem>
            )}
            {row.status !== CommentStatusEnum.REJECTED && (
              <DropdownItem
                className="dropdown-item"
                onClick={() => {
                  setSelectedComment(row);
                  setShowRejectModal(true);
                }}
              >
                {t("reject")}
              </DropdownItem>
            )}
            {row.status !== CommentStatusEnum.PENDING && (
              <DropdownItem
                className="dropdown-item"
                onClick={() => {
                  setSelectedComment(row);
                  setShowPendingModal(true);
                }}
              >
                {t("pending")}
              </DropdownItem>
            )}
            <DropdownItem
              className="dropdown-item text-danger"
              onClick={() => {
                setSelectedComment(row);
                setShowDeleteModal(true);
              }}
            >
              {t("delete")}
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      ),
    },
  ];

  const [defaultSortFieldId, setDefaultSortFieldId] = useState(1);
  const [defaultSortAsc, setDefaultSortAsc] = useState(false);
  const [defaultPage, setDefaultPage] = useState(1);

  useEffect(() => {
    if (!isUpdatingStatus && isUpdateStatusSuccess) {
      setSelectedComment(null);
      setShowApproveModal(false);
      setShowRejectModal(false);
      setShowPendingModal(false);
      refetch();
    }
  }, [isUpdatingStatus, isUpdateStatusSuccess]);

  useEffect(() => {
    if (!isDeleting && isDeleteSuccess) {
      setSelectedComment(null);
      setShowDeleteModal(false);
      refetch();
    }
  }, [isDeleting, isDeleteSuccess]);

  useEffect(() => {
    const queryParams = new URLSearchParams(history.location.search);
    const filters: { [column: string]: string | string[] } = {};
    if (queryParams.has("filter[status]")) {
      filters["status"] = queryParams.get("filter[status]") || "";
      setFilterStatusId(filters["status"] as string);
    }
    HIERARCHY_FILTER_KEYS.forEach((key) => {
      const param = queryParams.get(`filter[${key}]`);
      if (param) {
        filters[key] = param;
        if (key === "educationSystemId") setEducationSystemId(param);
        if (key === "curriculumId") setCurriculumId(param);
        if (key === "courseId") setCourseId(param);
        if (key === "stageId") setStageId(param);
        if (key === "gradeId") setGradeId(param);
        if (key === "subjectId") setSubjectId(param);
      }
    });
    const query: PaginateQuery = {
      limit: queryParams.has("limit")
        ? Number(queryParams.get("limit"))
        : paginateQuery.limit,
      page: queryParams.has("page")
        ? Number(queryParams.get("page"))
        : paginateQuery.page,
      sortBy: queryParams.has("sortBy")
        ? (queryParams.get("sortBy") as string)
        : paginateQuery.sortBy,
      search: queryParams.has("search")
        ? (queryParams.get("search") as string)
        : paginateQuery.search,
      filter: Object.keys(filters).length > 0 ? filters : paginateQuery.filter,
    };

    if (queryParams.get("sortBy")?.split(":")?.length === 2) {
      setDefaultSortAsc(
        (queryParams.get("sortBy")?.split(":")[1] as string) === "ASC",
      );
    }
    setDefaultPage(query.page);
    setPaginateQuery(query);
    setQueryReady(true);
  }, []);

  useEffect(() => {
    if (!queryReady) return;
    const { limit, page, search, sortBy } = paginateQuery;
    let query = page ? `?page=${page}` : "?page=1";
    if (limit) query += `&limit=${limit}`;
    if (sortBy) query += `&sortBy=${sortBy}`;
    if (search) query += `&search=${search}`;
    const filter = apiQuery.filter || {};
    Object.keys(filter).forEach((key) => {
      if (filter[key])
        query += `&filter[${key}]=${encodeURIComponent(filter[key] as string)}`;
    });
    history.replace({ search: query });
  }, [apiQuery, queryReady]);

  const onSearch = (searchText: string) => {
    setPaginateQuery({ ...paginateQuery, search: searchText, page: 1 });
    setDefaultPage(1);
  };

  const onChangePage = (page: number) => {
    setPaginateQuery({ ...paginateQuery, page });
  };

  const onChangeRowsPerPage = (
    currentRowsPerPage: number,
    currentPage: number,
  ) => {
    setPaginateQuery({
      ...paginateQuery,
      limit: currentRowsPerPage,
      page: currentPage,
    });
  };

  const onSort = (
    column: TableColumn<LectureCommentDetailDto>,
    sortDirection: string,
  ) => {
    const sortField = (column as any).id || "created_at";
    const sortBy = `${sortField}:${sortDirection.toUpperCase()}`;
    setPaginateQuery({ ...paginateQuery, sortBy });
  };

  const handleApprove = () => {
    if (!selectedComment) return;
    const dto: UpdateCommentStatusRequestDto = {
      status: CommentStatusEnum.APPROVED,
    };
    updateCommentStatus({ id: selectedComment.id, data: dto });
  };

  const handleReject = () => {
    if (!selectedComment) return;
    const dto: UpdateCommentStatusRequestDto = {
      status: CommentStatusEnum.REJECTED,
    };
    updateCommentStatus({ id: selectedComment.id, data: dto });
  };

  const handleMarkPending = () => {
    if (!selectedComment) return;
    const dto: UpdateCommentStatusRequestDto = {
      status: CommentStatusEnum.PENDING,
    };
    updateCommentStatus({ id: selectedComment.id, data: dto });
  };

  const handleDelete = () => {
    if (!selectedComment) return;
    deleteComment(selectedComment.id);
  };

  const data = queryData?.data || [];
  const meta = queryData?.meta;

  const onChangeStatusFilter = (value: string) => {
    setFilterStatusId(value);
    setPaginateQuery((prev) => ({ ...prev, page: 1 }));
    setDefaultPage(1);
  };

  const clearHierarchyFilters = () => {
    setEducationSystemId("");
    setCurriculumId("");
    setCourseId("");
    setStageId("");
    setGradeId("");
    setSubjectId("");
    setPaginateQuery((prev) => ({ ...prev, page: 1 }));
    setDefaultPage(1);
  };

  const onClearFilters = () => {
    setFilterStatusId("");
    clearHierarchyFilters();
  };

  const scopeHint =
    isHeadTeacherAdmin || isSystemAdmin
      ? t("commentReviewsAssignedEsHint", {
          defaultValue:
            "Showing comments under your assigned education systems.",
        })
      : isTeacherAdmin
        ? t("commentReviewsAssignedSubjectsHint", {
            defaultValue: "Showing comments for your assigned subjects.",
          })
        : null;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("commentReviews")} pageTitle={t("reviews")} />

          {((isHeadTeacherAdmin || isSystemAdmin) &&
            assignedEducationSystems.length > 0) ||
          (isTeacherAdmin && assignedCourses.length > 0) ? (
            <Card className="mb-3">
              <CardBody className="py-3">
                {isHeadTeacherAdmin || isSystemAdmin ? (
                  <>
                    <div className="fw-medium mb-2">
                      {t("assignedEducationSystems", {
                        defaultValue: "Assigned education systems",
                      })}
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      {assignedEducationSystems.map((system: SelectListDto) => (
                        <Badge
                          key={system.id}
                          color="primary"
                          className="fs-12 fw-normal"
                          pill
                        >
                          {labelOf(system, lang)}
                        </Badge>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="fw-medium mb-2">
                      {t("assignedCourses", {
                        defaultValue: "Assigned courses",
                      })}
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      {assignedCourses.map((course: SelectListDto) => (
                        <Badge
                          key={course.id}
                          color="info"
                          className="fs-12 fw-normal"
                          pill
                        >
                          {labelOf(course, lang)}
                        </Badge>
                      ))}
                    </div>
                  </>
                )}
                {scopeHint ? (
                  <div className="text-muted small mt-2">{scopeHint}</div>
                ) : null}
              </CardBody>
            </Card>
          ) : null}

          <Card className="mb-3">
            <CardBody>
              <div className="fw-medium mb-2">
                {t("filters", { defaultValue: "Filters" })}
              </div>
              <Row className="g-2 align-items-end">
                <Col md={2}>
                  <Label className="form-label mb-1">
                    {t("educationSystem")}
                  </Label>
                  <Input
                    type="select"
                    value={educationSystemId}
                    onChange={(e) => {
                      setEducationSystemId(e.target.value);
                      setCurriculumId("");
                      setStageId("");
                      setGradeId("");
                      setSubjectId("");
                      setPaginateQuery((prev) => ({ ...prev, page: 1 }));
                      setDefaultPage(1);
                    }}
                  >
                    <option value="">{t("all")}</option>
                    {(educationSystems || []).map((s: any) => (
                      <option key={s.id} value={s.id}>
                        {labelOf(s, lang)}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={2}>
                  <Label className="form-label mb-1">
                    {t("curriculumItem")}
                  </Label>
                  <Input
                    type="select"
                    value={curriculumId}
                    disabled={!educationSystemId}
                    onChange={(e) => {
                      setCurriculumId(e.target.value);
                      setStageId("");
                      setGradeId("");
                      setSubjectId("");
                      setPaginateQuery((prev) => ({ ...prev, page: 1 }));
                      setDefaultPage(1);
                    }}
                  >
                    <option value="">{t("all")}</option>
                    {(curricula || []).map((c: any) => (
                      <option key={c.id} value={c.id}>
                        {labelOf(c, lang)}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={2}>
                  <Label className="form-label mb-1">{t("course")}</Label>
                  <Input
                    type="select"
                    value={courseId}
                    onChange={(e) => {
                      setCourseId(e.target.value);
                      setStageId("");
                      setGradeId("");
                      setSubjectId("");
                      setPaginateQuery((prev) => ({ ...prev, page: 1 }));
                      setDefaultPage(1);
                    }}
                  >
                    <option value="">{t("all")}</option>
                    {(courses || []).map((c: any) => (
                      <option key={c.id} value={c.id}>
                        {labelOf(c, lang)}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={2}>
                  <Label className="form-label mb-1">
                    {t("educationalStage")}
                  </Label>
                  <Input
                    type="select"
                    value={stageId}
                    disabled={!curriculumId || !courseId}
                    onChange={(e) => {
                      setStageId(e.target.value);
                      setGradeId("");
                      setSubjectId("");
                      setPaginateQuery((prev) => ({ ...prev, page: 1 }));
                      setDefaultPage(1);
                    }}
                  >
                    <option value="">{t("all")}</option>
                    {stages.map((stage) => (
                      <option key={stage.id} value={stage.id}>
                        {stage.name[lang] || stage.name.en}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={2}>
                  <Label className="form-label mb-1">{t("grade")}</Label>
                  <Input
                    type="select"
                    value={gradeId}
                    disabled={!stageId}
                    onChange={(e) => {
                      setGradeId(e.target.value);
                      setSubjectId("");
                      setPaginateQuery((prev) => ({ ...prev, page: 1 }));
                      setDefaultPage(1);
                    }}
                  >
                    <option value="">{t("all")}</option>
                    {grades.map((grade) => (
                      <option key={grade.id} value={grade.id}>
                        {grade.name[lang] || grade.name.en}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={2}>
                  <Label className="form-label mb-1">{t("subject")}</Label>
                  <Input
                    type="select"
                    value={subjectId}
                    disabled={!gradeId}
                    onChange={(e) => {
                      setSubjectId(e.target.value);
                      setPaginateQuery((prev) => ({ ...prev, page: 1 }));
                      setDefaultPage(1);
                    }}
                  >
                    <option value="">{t("all")}</option>
                    {gradeSubjects.map((s: SelectListDto) => (
                      <option key={s.id} value={s.id}>
                        {labelOf(s, lang)}
                      </option>
                    ))}
                  </Input>
                </Col>
              </Row>
              {hasHierarchyFilters ? (
                <div className="mt-2 text-end">
                  <Button
                    color="link"
                    size="sm"
                    className="p-0"
                    onClick={clearHierarchyFilters}
                  >
                    {t("clearHierarchyFilters", {
                      defaultValue: "Clear hierarchy filters",
                    })}
                  </Button>
                </div>
              ) : null}
            </CardBody>
          </Card>

          <Row>
            <Col xxl={12}>
              <Card>
                <CardBody>
                  <Row className="g-2 mb-3">
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <DebounceInput
                          className="form-control"
                          placeholder={`${t("searchPlaceholder")}...`}
                          minLength={1}
                          debounceTimeout={300}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            onSearch(e.target.value)
                          }
                          value={paginateQuery.search || ""}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <select
                          value={filterStatusId}
                          className="form-select form-select-md"
                          onChange={(event: any) =>
                            onChangeStatusFilter(event.target.value)
                          }
                        >
                          <option value="">{t("filterByStatus")}</option>
                          <option value="pending">{t("pending")}</option>
                          <option value="approved">{t("approved")}</option>
                          <option value="rejected">{t("rejected")}</option>
                        </select>
                      </div>
                    </Col>
                    <Col className="col-sm-auto ms-auto">
                      <div className="list-grid-nav hstack gap-1">
                        <Button
                          color="success"
                          onClick={() => onClearFilters()}
                        >
                          {t("clearFilters")}
                        </Button>
                        <Button
                          color="success"
                          onClick={() => {
                            setPaginateQuery({ ...paginateQuery });
                            refetch();
                          }}
                        >
                          {t("refresh")}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={12} className="p-0">
                      <div className="table-responsive comment-reviews-table-wrap">
                        <AppTable
                          className="comment-reviews-table w-100"
                          theme="dark"
                          columns={columns}
                          data={data}
                          progressPending={isLoadingComments}
                          progressComponent={<TableSpinner />}
                          pagination
                          paginationServer
                          sortServer
                          onSort={onSort}
                          onChangeRowsPerPage={onChangeRowsPerPage}
                          onChangePage={onChangePage}
                          defaultSortFieldId={defaultSortFieldId}
                          paginationDefaultPage={defaultPage}
                          defaultSortAsc={defaultSortAsc}
                          persistTableHead
                          paginationTotalRows={meta?.totalItems || 0}
                          paginationComponentOptions={{
                            rowsPerPageText: t("rowsPerPage"),
                            rangeSeparatorText: t("of"),
                          }}
                          fixedHeader
                          fixedHeaderScrollHeight="60vh"
                        />
                      </div>
                    </Col>
                  </Row>

                  <ConfirmationModal
                    confirmationButtonInProgressTitle={t("saving")}
                    confirmationButtonTitle={t("approve")}
                    description={t("approveCommentConfirmation")}
                    icon={<></>}
                    title={t("areYouSure")}
                    loading={isUpdatingStatus}
                    show={showApproveModal}
                    onConfirmClick={handleApprove}
                    onCloseClick={() => {
                      setShowApproveModal(false);
                      setSelectedComment(null);
                    }}
                  />

                  <ConfirmationModal
                    confirmationButtonInProgressTitle={t("saving")}
                    confirmationButtonTitle={t("reject")}
                    description={t("rejectCommentConfirmation")}
                    icon={<></>}
                    title={t("areYouSure")}
                    loading={isUpdatingStatus}
                    show={showRejectModal}
                    onConfirmClick={handleReject}
                    onCloseClick={() => {
                      setShowRejectModal(false);
                      setSelectedComment(null);
                    }}
                  />

                  <ConfirmationModal
                    confirmationButtonInProgressTitle={t("saving")}
                    confirmationButtonTitle={t("pending")}
                    description={t("markPendingCommentConfirmation")}
                    icon={<></>}
                    title={t("areYouSure")}
                    loading={isUpdatingStatus}
                    show={showPendingModal}
                    onConfirmClick={handleMarkPending}
                    onCloseClick={() => {
                      setShowPendingModal(false);
                      setSelectedComment(null);
                    }}
                  />

                  <ConfirmationModal
                    confirmationButtonInProgressTitle={t("deleting")}
                    confirmationButtonTitle={t("delete")}
                    description={t("deleteCommentConfirmation")}
                    icon={<></>}
                    title={t("areYouSure")}
                    loading={isDeleting}
                    show={showDeleteModal}
                    onConfirmClick={handleDelete}
                    onCloseClick={() => {
                      setShowDeleteModal(false);
                      setSelectedComment(null);
                    }}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};
